import { useEffect, useState, useCallback } from "react";
import ky from "ky-universal";
import { DDXModal } from "components/ddx-modal/DDXModal";
import ButtonAction from "components/utility/button/ButtonAction";
import { getDecodedHtml } from "utils/getDecodedHtml";
import Dropdown from "components/utility/dropdown/Dropdown";
import UnauthenticatedContactUsModalForm from "./UnauthenticatedContactUsModalForm";
import ImageX from "components/utility/images/ImageX";
import { Scrollbars } from "react-custom-scrollbars-2";
import { FormDataProps, MobileCountriesListProps } from "./UnauthenticatedContactUsModalTypes";

const UnauthenticatedContactUsModal = () => {
	const [mobileCountriesList, setMobileCountriesList] = useState<MobileCountriesListProps[]>([]);
	const [formData, setFormData] = useState<FormDataProps>({
		countryList: [],
		countryListLabel: "",
		countryListTopLabel: "",
		countrySelectText: "",
		failedMessage: "",
		footerContent: "",
		formSubmitURL: "",
		header: "",
		id: "",
		nonce: "",
		successMessage: "",
		title: ""
	});
	const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
	const [selectedCountry, setSelectedCountry] = useState({ caption: "" });
	const [countryFlag, setCountryFlag] = useState<boolean>(false);
	const [formActive, setFormActive] = useState<boolean>(false);
	const [returnedToCountrySelection, setReturnedToCountrySelection] = useState<boolean>(false);
	const [sentFlag, setSentFlag] = useState<boolean>(false);
	const [professionalInvestor, setProfessionalInvestor] = useState<boolean>(false);
	const [showActionButtons, setShowActionsButtons] = useState<boolean>(false);
	const [countryListToggle, setCountryListToggle] = useState<boolean>(false);
	const [sentError, setSentError] = useState<boolean>(false);
	const [selectedCountryIndex, setSelectedCountryIndex] = useState<number | string>(0);
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const [dataId, setDataId] = useState<string>("");

	const onResize = () => {
		const windowWidth = window.innerWidth;
		setIsMobile(windowWidth <= 767 ? true : false);
	};

	const initializeModal = () => {
		setSelectedCountry({ caption: "" });
		setCountryFlag(false);
		setFormActive(false);
		setReturnedToCountrySelection(false);
		setSentFlag(false);
		setProfessionalInvestor(false);
		setShowActionsButtons(false);
		setModalIsOpen(false);
		setCountryListToggle(false);
		setSentError(false);
	};

	const closeModal = () => {
		setModalIsOpen(false);
		setShowActionsButtons(false);
	};

	const initializeMobileCountries = useCallback(() => {
		let initMobileCountriesList: MobileCountriesListProps[] = [];
		if (formData.countryList.length) {
			formData.countryList?.forEach((e) => {
				let countryObject = {
					country: e.key,
					selected: false
				};
				initMobileCountriesList.push(countryObject);
			});
			setMobileCountriesList(initMobileCountriesList);
		}
	}, [formData.countryList]);

	const onCountryClose = (item) => {
		let selectedCountryIndexItem: number | string = formData.countryList.findIndex(
			(i) => i.key === item.key
		);
		if (selectedCountryIndexItem === -1) {
			selectedCountryIndexItem = "";
			return;
		}
		if (isMobile) {
			setSelectedCountry({ caption: item.caption });
		} else {
			setSelectedCountry(item);
		}
		setFormActive(true);
		setSelectedCountryIndex(selectedCountryIndexItem);
	};

	const onCountrySelect = (country) => {
		resetMobileCountryList();
		let selectedCountryIndexItem = mobileCountriesList.findIndex((i) => i.country === country.key);
		mobileCountriesList[selectedCountryIndexItem].selected = true;
		setMobileCountriesList(mobileCountriesList);
		setSelectedCountry(country);
		setShowActionsButtons(true);
		setFormActive(true);
		setSelectedCountryIndex(selectedCountryIndexItem);
	};

	const isCountryList = () => {
		setCountryListToggle(!countryListToggle);
	};

	const initialScreenReturn = () => {
		setCountryListToggle(false);
		setShowActionsButtons(false);
		setReturnedToCountrySelection(false);
		resetMobileCountryList();
	};

	const resetMobileCountryList = () => {
		mobileCountriesList.forEach((e) => {
			e.selected = false;
		});
	};

	const onFocus = () => {
		let btn = document.querySelector(".contact-us-modal-user-type-buttons > button:first-of-type");
		if (btn instanceof HTMLElement) {
			btn?.focus();
		}
	};

	const keyUpHandler = (e, eventType, country = {}) => {
		const key = e.which || e.keyCode || 0;
		if (key === 13 || key === 32) {
			e.preventDefault();
			switch (eventType) {
				case "countryListToggle": {
					isCountryList();
					break;
				}
				case "initialScreenReturn": {
					initialScreenReturn();
					break;
				}
				case "countrySelect": {
					onCountrySelect(country);
					onFocus();
					break;
				}
				default: {
					break;
				}
			}
		}
	};

	useEffect(() => {
		const openModal = () => {
			initializeModal();
			setModalIsOpen(true);
		};
		document.querySelector(".content-root")?.addEventListener("click", (e) => {
			const target = e.target as HTMLElement;
			if (target.hasAttribute("data-modal")) {
				e.preventDefault();
				let id = target.getAttribute("data-id");
				let values = { id: id };
				if (id) {
					setDataId(id);
				}
				const api =
					process.env.NODE_ENV === "development"
						? "http://localhost:7000/unauthed-contact-us-data"
						: `${window.location.origin}/contact-api/UnAuthenticatedContactUs/contact-us?id=${id}`;
				const getFormData = async () => {
					await ky
						.post(api, {
							headers: new Headers({
								"Content-Type": "application/json"
							}),
							body: JSON.stringify(values)
						})
						.then((res) => res.json<FormDataProps>())
						.then((res) => {
							setFormData(res);
							let initMobileCountriesList: MobileCountriesListProps[] = [];
							res.countryList?.forEach((e) => {
								let countryObject = {
									country: e.key,
									selected: false
								};
								initMobileCountriesList.push(countryObject);
							});
							setMobileCountriesList(initMobileCountriesList);
							openModal();
						});
				};
				getFormData();
			}
		});
		window.addEventListener("resize", onResize);
	}, [initializeMobileCountries, selectedCountryIndex]);

	const returnToCountrySelection = (e) => {
		e.preventDefault();
		let mobileFormActive = isMobile ? true : false;
		let mobileSelectedCountry = isMobile ? selectedCountry : { caption: "" };
		setCountryFlag(false);
		setSelectedCountry(mobileSelectedCountry);
		setReturnedToCountrySelection(true);
		setProfessionalInvestor(false);
		setFormActive(mobileFormActive);
		setShowActionsButtons(true);
	};

	const onIndividualButtonClick = () => {
		if (selectedCountry.caption !== "") {
			setCountryFlag(true);
			setShowActionsButtons(false);
		} else {
			return;
		}
	};

	const onProfessionalButtonClick = () => {
		if (selectedCountry.caption !== "") {
			setCountryFlag(true);
			setProfessionalInvestor(true);
			setShowActionsButtons(false);
		} else {
			return;
		}
	};

	return (
		<>
			{formData.countryList?.length ? (
				<DDXModal
					showModal={modalIsOpen}
					handleCloseModal={closeModal}
					ariaModal={true}
					ariaLabel="contact-us"
					className={`contact-us-modal`}
				>
					<>
						<div className="contact-us-modal-close-container">
							<button className="contact-us-modal-close-btn" onClick={closeModal}>
								<ImageX />
							</button>
						</div>
						<div
							className={`contact-us-modal-mobile-overlay ${
								sentFlag ? `contact-us-modal-mobile-overlay--shown` : ``
							}`}
						>
							<div className="contact-us-modal-mobile-overlay-content">
								{sentError ? (
									<div>
										<h1
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.failedMessage) }}
										/>
										<p
											className="contact-us-modal-right-content-thanks-primary-text"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex].error.message
												)
											}}
										/>
									</div>
								) : (
									<div>
										<h1
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.successMessage) }}
										/>
										<p
											className="contact-us-modal-right-content-thanks-primary-text"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex]?.success.message
												)
											}}
										/>
										<p
											className="contact-us-modal-right-content-thanks-footer"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex]?.success.footer
												)
											}}
										/>
										<p
											className="contact-us-modal-right-content-tablet-form-footer"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.footerContent) }}
										/>
									</div>
								)}
							</div>
						</div>
						<div className="contact-us-modal-left-content">
							{sentFlag ? (
								sentError ? (
									<div className="contact-us-modal-left-content-wrapper contact-us-modal-left-content-thanks">
										<h1
											className="contact-us-modal-left-content-header contact-us-modal-left-content-header--thanks"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.failedMessage) }}
										/>
										<p
											className="contact-us-modal-left-content__footer"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.footerContent) }}
										/>
									</div>
								) : (
									<div className="contact-us-modal-left-content-wrapper contact-us-modal-left-content-thanks">
										<h1
											className="contact-us-modal-left-content-header contact-us-modal-left-content-header--thanks"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.successMessage) }}
										/>
										<p
											className="contact-us-modal-left-content-footer"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.footerContent) }}
										/>
									</div>
								)
							) : (
								<div className="contact-us-modal-left-content-wrapper">
									<p
										className="contact-us-modal-left-content-title"
										dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.title) }}
									/>
									<h1
										className={`contact-us-modal-left-content-header ${
											countryFlag ? `contact-us-modal-left-content-header--form` : ``
										}`}
										dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.header) }}
									/>
									<p
										className="contact-us-modal-left-content-footer"
										dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.footerContent) }}
									/>
								</div>
							)}
						</div>
						<div
							className={`contact-us-modal-right-content ${
								countryFlag ? `contact-us-modal-right-content--post-submit` : ``
							}`}
						>
							{sentFlag ? (
								sentError ? (
									<div className="contact-us-modal-right-content-thanks">
										<p
											className="contact-us-modal-right-content-thanks-primary-text"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex].error.message
												)
											}}
										/>
									</div>
								) : (
									<div className="contact-us-modal-right-content-thanks">
										<p
											className="contact-us-modal-right-content-thanks-primary-text"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex].success.message
												)
											}}
										/>
										<p
											className="contact-us-modal-right-content-thanks-footer"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex].success.footer
												)
											}}
										/>
										<p
											className="contact-us-modal-right-content-tablet-form-footer"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.footerContent) }}
										/>
									</div>
								)
							) : countryFlag ? (
								professionalInvestor ? (
									<div
										className={`contact-us-modal-form-content contact-us-modal-user-info-form modal-form-active ${
											isMobile
												? countryFlag
													? `contact-us-modal-user-info-form--mobile-shown`
													: ``
												: ``
										}`}
									>
										<a
											href="/"
											className="contact-us-modal-return"
											onClick={returnToCountrySelection}
										>
											{selectedCountry.caption}
										</a>
										<UnauthenticatedContactUsModalForm
											data={formData.countryList[selectedCountryIndex]}
											formSubmitURL={formData.formSubmitURL}
											nonce={formData.nonce}
											selectedCountry={selectedCountry}
											sentFlag={setSentFlag}
											sentError={setSentError}
											showBusinessNameField={
												formData.countryList[selectedCountryIndex].showBusinessNameField
											}
											submitId={dataId}
										/>
										<p
											className="contact-us-modal-right-content-footer"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(
													formData.countryList[selectedCountryIndex].formFooterContent
												)
											}}
										/>
										<p
											className="contact-us-modal-right-content-tablet-form-footer"
											dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.footerContent) }}
										/>
									</div>
								) : (
									<div className={`contact-us-modal-form-content contact-us-modal-individual`}>
										<a
											href="/"
											className="contact-us-modal-return"
											onClick={returnToCountrySelection}
										>
											{selectedCountry.caption}
										</a>
										<div className="contact-us-modal-individual-content">
											<p
												className="individual-investor-text"
												dangerouslySetInnerHTML={{
													__html: getDecodedHtml(
														formData.countryList[selectedCountryIndex].individualInvestorMessage
													)
												}}
											/>
										</div>
									</div>
								)
							) : countryListToggle ? (
								<div
									className={`contact-us-modal-mobile-country-selector-country-list-container ${
										returnedToCountrySelection
											? `contact-us-modal-mobile-country-selector-country-list-container--return`
											: ``
									}`}
								>
									<Scrollbars autoHide autoHideTimeout={1000} autoHideDuration={200}>
										<div>
											<div
												aria-label={formData.countryListLabel}
												role="button"
												tabIndex={0}
												onClick={initialScreenReturn}
												onKeyUp={(event) => keyUpHandler(event, "initialScreenReturn")}
											>
												<div className="contact-us-modal-mobile-country-selector-mobile-title">
													{formData.countryListLabel}
												</div>
											</div>
											<div
												className={`contact-us-modal-mobile-country-selector-list-item-container ${
													showActionButtons ? `list-item-container--bottom-padding` : ``
												}`}
											>
												{formData.countryList.map((country, index) => {
													return (
														<div
															className={`contact-us-modal-mobile-country-selector-list-item ${
																mobileCountriesList[index]?.selected ? `list-item--checked` : ``
															}`}
															key={index}
															aria-label={country.caption}
															role="button"
															tabIndex={0}
															onClick={() => onCountrySelect(country)}
															onKeyUp={(event) => keyUpHandler(event, "countrySelect", country)}
														>
															{country.caption}
														</div>
													);
												})}
											</div>
										</div>
									</Scrollbars>

									<div
										className={`contact-us-modal-user-type-buttons ${
											showActionButtons ? `` : `contact-us-modal-user-type-buttons--hidden`
										}`}
									>
										<ButtonAction
											type="solid"
											width="full"
											isButton={true}
											label="Investment Professionals"
											onClick={onProfessionalButtonClick}
											disabled={!formActive}
											dataA="true"
											dataALbl={selectedCountry.caption + " Submit"}
											dataACat="Forms"
											dataAAct="Contact Us"
										/>
										<ButtonAction
											type="outline"
											width="full"
											isButton={true}
											label="Individual Investors"
											onClick={onIndividualButtonClick}
											disabled={!formActive}
											dataA="true"
											dataALbl={selectedCountry.caption + " Alternative Option Redirect"}
											dataACat="Forms"
											dataAAct="Contact Us"
										/>
									</div>
								</div>
							) : (
								<div
									className={`contact-us-modal-form-content contact-us-modal-country-select-question ${
										formActive ? `slide-out-mobile` : ``
									}`}
								>
									<p
										className="contact-us-modal-country-select-question-primary-text"
										dangerouslySetInnerHTML={{ __html: getDecodedHtml(formData.countrySelectText) }}
									/>
									<div
										className={`contact-us-modal-mobile-country-selector-button ${
											isMobile ? `` : `mobile-country-selector--shown`
										}`}
										role="button"
										tabIndex={0}
										aria-label={formData.countryListLabel}
										onClick={isCountryList}
										onKeyUp={(event) => keyUpHandler(event, "countryListToggle")}
									>
										{formData.countryListLabel}
									</div>
									<Dropdown
										items={formData.countryList}
										topLabel={formData.countryListTopLabel}
										label={formData.countryListLabel}
										name="country"
										currentItem={selectedCountry}
										containerClassName="contact-us-modal-country-dropdown"
										onClose={onCountryClose}
									/>

									<div className="contact-us-modal-action-buttons">
										<ButtonAction
											type="solid"
											width="full"
											isButton={true}
											label="Investment Professionals"
											onClick={onProfessionalButtonClick}
											disabled={!formActive}
											dataA="true"
											dataALbl={selectedCountry.caption + " Form Started"}
											dataACat="Forms"
											dataAAct="Contact Us"
										/>
										<ButtonAction
											type="outline"
											width="full"
											isButton={true}
											label="Individual Investors"
											onClick={onIndividualButtonClick}
											disabled={!formActive}
											dataA="true"
											dataALbl={selectedCountry.caption + " Alternative Option Redirect"}
											dataACat="Forms"
											dataAAct="Contact Us"
										/>
									</div>
								</div>
							)}
						</div>
					</>
				</DDXModal>
			) : null}
		</>
	);
};

export default UnauthenticatedContactUsModal;
